import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import HeaderDescription from "../../../../components/commons/HeaderDescription";
import { adminOnboardingVehicleInfoSchema } from "../../../../validations/adminOnboarding";
import MOButtonV2 from "../../../../components/commons/MOButtonV2";
import mobioApi from "../../../../services";
import { toastMessage } from "../../../../helpers/toastMessage";

const VehicleInfo = ({ onboardingData, token }) => {
  const [years, setYears] = useState([]);
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brandList, setBrandList] = useState([]);
  const [brandCarList, setBrandCarList] = useState([]);

  // api handlers
  const getBrandListAPIHandler = async () => {
    try {
      setLoading(true);

      const api = mobioApi(token);
      const response = await api.getBrandList();

      if (response?.data?.data) {
        setBrandList(response.data.data);
      } else {
        setBrandList([]);
      }
    } catch (error) {
      setBrandList([]);
    } finally {
      setLoading(false);
    }
  };

  const getBrandCarModelListAPIHandler = async (id) => {
    try {
      setLoading(true);

      const api = mobioApi(token);
      const response = await api.getBrandCarList(id);

      if (response?.data?.data) {
        setBrandCarList(response.data.data);
      } else {
        setBrandCarList([]);
      }
    } catch (error) {
      setBrandCarList([]);
    } finally {
      setLoading(false);
    }
  };

  // Generate dropdown data
  useEffect(() => {
    getBrandListAPIHandler();
    if (onboardingData?.brand?.id) {
      getBrandCarModelListAPIHandler(onboardingData?.brand?.id);
    }
    const allYears = [];
    for (let year = 2000; year <= new Date().getFullYear(); year++) {
      allYears.push(year);
    }
    setYears(allYears);

    const allSeats = [];
    for (let seat = 1; seat <= 8; seat++) {
      allSeats.push(seat);
    }
    setSeats(allSeats);
  }, []);

  const serviceLevels = [
    "STANDARD",
    "EXECUTIVE",
    "ELECTRIC",
    "MINI_BUS",
    "CARRIER",
    "LARGE_CARRIER",
    "EXE_CARRIER",
    "LUXURY",
    "ELECTRIC_LUXURY",
    "ELECTRIC_PEOPLE_CARRIER",
    "ELECTRIC_EXE_PEOPLE_CARRIER",
    "ELECTRIC_LARGE_PEOPLE_CARRIER",
  ];

  return (
    <div className="bg-white border-radius-8 ">
      <div className="width-ful">
        <Formik
          initialValues={{
            numberPlate: onboardingData
              ? onboardingData?.numberPlate || ""
              : "",
            brandName: onboardingData ? onboardingData?.brand?.id || "" : "",
            carName: onboardingData ? onboardingData?.car?.id || "" : "",
            color: onboardingData ? onboardingData?.car?.color || "" : "",
            year: onboardingData ? onboardingData?.car?.year || "" : "",
            serviceLevel: onboardingData
              ? onboardingData?.car?.category || ""
              : "",
            seatCapacity: onboardingData
              ? onboardingData?.car?.seatingCapacity || ""
              : "",
          }}
          validationSchema={adminOnboardingVehicleInfoSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const payload = {
                id: onboardingData.id,
                numberPlate: values.numberPlate,
                carId: values.carName,
                brandId: values.brandName,
              };

              const api = mobioApi(token);
              const response = await api.onboardingVehicleInfoUpdate(payload);
              if (response?.data?.success) {
                toastMessage(
                  "success",
                  response?.data?.msg || "User Vehicle data update successfully"
                );

                onboardingData.numberPlate = values.numberPlate;
                onboardingData.brand.id = values.brandName;
                onboardingData.car.id = values.carName;
                onboardingData.brandId = values.brandName;
                onboardingData.carId = values.carName;
              }
            } catch (error) {
              toastMessage("error", error?.response?.data.msg);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form>
              <div className="px-28 pb-40 pt-24">
                <HeaderDescription
                  heading="Vehicle Information"
                  description="Enter vehicle details including model, service level, and capacity."
                />

                <div className="mt-20">
                  {/* Row 1 */}
                  <div className="flex align-item-center gap-32">
                    <div className="flex-1">
                      <div className="input-field-md-lg">
                        <label>Number Plate</label>
                        <Field
                          type="text"
                          name="numberPlate"
                          placeholder="Enter Number Plate"
                          className="type-input-focus"
                          value={values.numberPlate}
                        />
                        <ErrorMessage
                          name="numberPlate"
                          component="p"
                          className="text-danger font-10"
                        />
                      </div>
                    </div>

                    <div className="flex-1">
                      {/* <div className="input-field-md-lg">
                        <label>Brand Name</label>
                        <Field
                          type="text"
                          name="brandName"
                          placeholder="Enter Brand Name"
                          className="type-input-focus"
                          value={values.brandName}
                        />
                        <ErrorMessage
                          name="brandName"
                          component="p"
                          className="text-danger font-10"
                        />
                      </div> */}

                      <div className="input-field-md-lg">
                        <label>Brand Name</label>
                        <Field
                          as="select"
                          name="brandName"
                          className="type-input-focus"
                          value={values.brandName}
                          onChange={async (e) => {
                            setFieldValue("brandName", e.target.value);
                            setFieldValue("color", "");
                            setFieldValue("year", "");
                            setFieldValue("serviceLevel", "");
                            setFieldValue("seatCapacity", "");
                            await getBrandCarModelListAPIHandler(
                              e.target.value
                            );
                          }}
                        >
                          <option value="">Select Brand</option>
                          {brandList.map((brand) => (
                            <option key={brand.id} value={brand.id}>
                              {brand.brandName}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="brandName"
                          component="p"
                          className="text-danger font-10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="flex align-item-center gap-32 mt-20">
                    <div className="flex-1">
                      {/* <div className="input-field-md-lg">
                        <label>Car Name</label>
                        <Field
                          type="text"
                          name="carName"
                          placeholder="Enter Car Name"
                          className="type-input-focus"
                          value={values.carName}
                        />
                        <ErrorMessage
                          name="carName"
                          component="p"
                          className="text-danger font-10"
                        />
                      </div> */}

                      <div className="input-field-md-lg">
                        <label>Car Name</label>
                        <Field
                          as="select"
                          name="carName"
                          className="type-input-focus"
                          value={values.carName}
                          onChange={async (e) => {
                            setFieldValue("carName", e.target.value);
                            const dataBrandCar = brandCarList?.find(
                              (bf) => bf.id === e.target.value
                            );
                            if (dataBrandCar) {
                              setFieldValue("color", dataBrandCar.color);
                              setFieldValue("year", dataBrandCar.year);
                              setFieldValue(
                                "serviceLevel",
                                dataBrandCar.category
                              );
                              setFieldValue(
                                "seatCapacity",
                                dataBrandCar.seatingCapacity
                              );
                            }
                          }}
                        >
                          <option value="">Select Car Name</option>
                          {brandCarList.map((car) => (
                            <option key={car.id} value={car.id}>
                              {`${car.carName} - ${car.year} - ${car.category} - ${car.seatingCapacity}`}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="carName"
                          component="p"
                          className="text-danger font-10"
                        />
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="input-field-md-lg">
                        <label>Color</label>
                        <Field
                          type="text"
                          name="color"
                          placeholder="Enter Color"
                          className="type-input-focus"
                          value={values.color}
                          disabled={true}
                        />
                        {/* <ErrorMessage
                          name="color"
                          component="p"
                          className="text-danger font-10"
                        /> */}
                      </div>
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div className="flex align-item-center gap-32 mt-20">
                    <div className="flex-1">
                      {/* <div className="input-field-md-lg">
                        <label>Year</label>
                        <Field
                          as="select"
                          name="year"
                          className="type-input-focus"
                          value={values.year}
                        >
                          <option value="">Select Year</option>
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="year"
                          component="p"
                          className="text-danger font-10"
                        />
                      </div> */}

                      <div className="input-field-md-lg">
                        <label>Year</label>
                        <Field
                          type="text"
                          name="year"
                          // placeholder="Year"
                          className="type-input-focus"
                          value={values.year}
                          disabled={true}
                        />
                      </div>
                    </div>

                    <div className="flex-1">
                      {/* <div className="input-field-md-lg">
                        <label>Service Level</label>
                        <Field
                          as="select"
                          name="serviceLevel"
                          className="type-input-focus"
                          value={values.serviceLevel}
                        >
                          <option value="">Select Service Level</option>
                          {serviceLevels.map((level) => (
                            <option key={level} value={level}>
                              {level.replaceAll("_", " ")}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="serviceLevel"
                          component="p"
                          className="text-danger font-10"
                        />
                      </div> */}

                      <div className="input-field-md-lg">
                        <label>Service Level</label>
                        <Field
                          type="text"
                          name="serviceLevel"
                          className="type-input-focus"
                          value={values.serviceLevel}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 4 */}
                  <div className="flex align-item-center gap-32 mt-20">
                    <div className="flex-1">
                      {/* <div className="input-field-md-lg">
                        <label>Seat Capacity</label>
                        <Field
                          as="select"
                          name="seatCapacity"
                          className="type-input-focus"
                          value={values.seatCapacity}
                        >
                          <option value="">Select Seat Capacity</option>
                          {seats.map((seat) => (
                            <option key={seat} value={seat}>
                              {seat}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="seatCapacity"
                          component="p"
                          className="text-danger font-10"
                        />
                      </div> */}

                      <div className="input-field-md-lg">
                        <label>Seat Capacity</label>
                        <Field
                          type="text"
                          name="seatCapacity"
                          className="type-input-focus"
                          value={values.seatCapacity}
                          disabled={true}
                        />
                      </div>
                    </div>

                    <div className="flex-1"></div>
                  </div>

                  <div className="flex align-item-center justify-content-end mt-42">
                    <MOButtonV2
                      type="submit"
                      text="SAVE VEHICLE INFORMATION"
                      textColor="#ffffff"
                      radius={4}
                      widthSize={200}
                      height={42}
                      padding={"10px 12px 10px 12px"}
                      backgroundColor="#29AEE6"
                      fontSize={12}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default VehicleInfo;
